import React, { Component } from 'react';
import moment from 'moment';

const DATETIME_INPUT = 'YYYY-MM-DDTHH:mm';
const API_DATETIME = 'YYYY-MM-DD HH:mm:ss';

const isMidnight = (m) =>
  m && m.isValid() && m.hours() === 0 && m.minutes() === 0 && m.seconds() === 0;

export const toDatetimeLocalValue = (value) => {
  if (value == null || value === '') return '';
  const m = moment(value);
  if (!m.isValid()) return '';
  return m.format(DATETIME_INPUT);
};

export const formatRangeModelText = (model) => {
  if (!model) return '';
  const from = model.dateFrom ? moment(model.dateFrom) : null;
  const to = model.dateTo ? moment(model.dateTo) : null;
  const lang = localStorage.getItem('i18nextLng');
  const fmt = lang === 'en' ? 'MM/DD/YYYY HH:mm' : 'DD/MM/YYYY HH:mm';
  const fromText = from && from.isValid() ? from.format(fmt) : '';
  const toText = to && to.isValid() ? to.format(fmt) : '';
  if (fromText && toText) return `${fromText} – ${toText}`;
  return fromText || toText || '';
};

const endOfSelectedMinute = (m) => {
  const end = m.clone();
  if (end.seconds() === 0 && end.milliseconds() === 0) {
    end.seconds(59).milliseconds(999);
  }
  return end;
};

/**
 * Client-side match for the From / To datetime filter.
 * Bounds are inclusive. A minute-precision "To" includes that whole minute.
 * Empty cell values do not match an active range.
 */
export const doesDateRangeMatch = (value, dateFrom, dateTo) => {
  const from = dateFrom ? moment(dateFrom) : null;
  const to = dateTo ? moment(dateTo) : null;
  const hasFrom = !!(from && from.isValid());
  const hasTo = !!(to && to.isValid());
  if (!hasFrom && !hasTo) return true;

  if (value == null || value === '') return false;
  const cell = moment(value);
  if (!cell.isValid()) return false;
  if (hasFrom && cell.isBefore(from)) return false;
  if (hasTo && cell.isAfter(endOfSelectedMinute(to))) return false;
  return true;
};

const readFilterCellValue = (params, props) => {
  if (props && typeof props.getValue === 'function' && params && params.node) {
    try {
      const fromGrid = props.getValue(params.node);
      if (fromGrid != null && fromGrid !== '') return fromGrid;
    } catch (e) {
      // Fall through to the row field.
    }
  }
  const data =
    (params && params.data) ||
    (params && params.node && params.node.data) ||
    null;
  const field = props && props.colDef && props.colDef.field;
  if (field && data && data[field] != null) return data[field];
  return null;
};

const readRangeFromModel = (model) => {
  if (!model) return { dateFrom: '', dateTo: '' };

  let dateFrom = model.dateFrom || '';
  let dateTo = model.dateTo || '';

  const conditions = [];
  if (Array.isArray(model.conditions)) {
    model.conditions.forEach((c) => conditions.push(c));
  } else {
    if (model.condition1) conditions.push(model.condition1);
    if (model.condition2) conditions.push(model.condition2);
  }

  conditions.forEach((c) => {
    if (!c) return;
    if (c.type === 'inRange') {
      dateFrom = c.dateFrom || dateFrom;
      dateTo = c.dateTo || dateTo;
    } else if (
      c.type === 'fromdate' ||
      c.type === 'greaterThan' ||
      c.type === 'greaterThanOrEqual'
    ) {
      dateFrom = c.dateFrom || dateFrom;
    } else if (
      c.type === 'todate' ||
      c.type === 'lessThan' ||
      c.type === 'lessThanOrEqual'
    ) {
      dateTo = c.dateFrom || c.dateTo || dateTo;
    }
  });

  return { dateFrom, dateTo };
};

/**
 * From / To datetime filter.
 * Server-side grids send the model to the API. Client-side grids (Studio
 * templates and other rowData tables) apply the same range in doesFilterPass.
 */
class DateRangeFilter extends Component {
  constructor(props) {
    super(props);
    const initial = readRangeFromModel(props.model);
    this.dateFrom = toDatetimeLocalValue(initial.dateFrom);
    this.dateTo = toDatetimeLocalValue(initial.dateTo);
    this.state = {
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    };
    this.fromInputRef = React.createRef();
    this.notifyTimer = null;
  }

  componentWillUnmount() {
    if (this.notifyTimer) clearTimeout(this.notifyTimer);
  }

  isFilterActive() {
    return !!(this.dateFrom || this.dateTo);
  }

  doesFilterPass(params) {
    if (!this.isFilterActive()) return true;
    return doesDateRangeMatch(
      readFilterCellValue(params, this.props),
      this.dateFrom,
      this.dateTo
    );
  }

  getModel() {
    if (!this.isFilterActive()) return null;
    return {
      filterType: 'date',
      type: 'inRange',
      dateFrom: this.dateFrom
        ? moment(this.dateFrom).format(API_DATETIME)
        : null,
      dateTo: this.dateTo
        ? moment(this.dateTo).format(API_DATETIME)
        : null,
    };
  }

  setModel(model) {
    const range = readRangeFromModel(model);
    this.dateFrom = toDatetimeLocalValue(range.dateFrom);
    this.dateTo = toDatetimeLocalValue(range.dateTo);
    this.setState({
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    });
  }

  getModelAsString(model) {
    return formatRangeModelText(model || this.getModel());
  }

  afterGuiAttached() {
    if (this.fromInputRef.current) {
      this.fromInputRef.current.focus();
    }
  }

  scheduleNotify = () => {
    if (this.notifyTimer) clearTimeout(this.notifyTimer);
    this.notifyTimer = setTimeout(() => {
      this.notifyTimer = null;
      if (this.props.filterChangedCallback) {
        this.props.filterChangedCallback();
      }
    }, 400);
  };

  notifyNow = () => {
    if (this.notifyTimer) {
      clearTimeout(this.notifyTimer);
      this.notifyTimer = null;
    }
    if (this.props.filterChangedCallback) {
      this.props.filterChangedCallback();
    }
  };

  onFromChange = (event) => {
    this.dateFrom = event.target.value;
    this.setState({ dateFrom: this.dateFrom }, this.scheduleNotify);
  };

  onToChange = (event) => {
    const value = event.target.value;
    const prev = this.dateTo;
    let next = value;
    const parsed = moment(value);
    if (!prev && parsed.isValid() && isMidnight(parsed)) {
      next = parsed.clone().hours(23).minutes(59).format(DATETIME_INPUT);
    }
    this.dateTo = next;
    this.setState({ dateTo: this.dateTo }, this.scheduleNotify);
  };

  onClear = () => {
    this.dateFrom = '';
    this.dateTo = '';
    this.setState({ dateFrom: '', dateTo: '' }, this.notifyNow);
  };

  render() {
    return (
      <div className="ilocal-date-range-filter">
        <label>
          From
          <input
            ref={this.fromInputRef}
            type="datetime-local"
            step="60"
            value={this.state.dateFrom}
            onChange={this.onFromChange}
            aria-label="From date and time"
          />
        </label>
        <label>
          To
          <input
            type="datetime-local"
            step="60"
            value={this.state.dateTo}
            onChange={this.onToChange}
            aria-label="To date and time"
          />
        </label>
        <div className="ilocal-date-range-filter-actions">
          <button
            type="button"
            className="ag-standard-button ag-filter-apply-panel-button"
            onClick={this.onClear}
          >
            Clear
          </button>
        </div>
      </div>
    );
  }
}

class DateRangeFloatingFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      text: formatRangeModelText(props.model || props.currentParentModel),
    };
  }

  onParentModelChanged(parentModel) {
    this.setState({ text: formatRangeModelText(parentModel) });
  }

  render() {
    return (
      <div className="ilocal-date-range-floating">
        <input
          className="ag-input-field-input ag-text-field-input"
          readOnly
          value={this.state.text}
          placeholder="From – To"
          title={this.state.text || 'From – To'}
          onClick={() => {
            if (this.props.showParentFilter) this.props.showParentFilter();
          }}
          aria-label="Date range filter"
        />
      </div>
    );
  }
}

export { DateRangeFilter, DateRangeFloatingFilter };
export default DateRangeFilter;



// WEBPACK FOOTER //
// ./src/components/grid/DateRangeFilter.js